import { inject, Injectable } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isEmpty } from 'lodash';
import { defer, of } from 'rxjs';
import { map, pairwise, tap } from 'rxjs/operators';
import { UpdateMetadataOperation } from '../../../../team-apis/otterdam/annotation-operations/metadata-operations';
import { TaskWorkspaceState } from '../../../h3ml/state/task-workspace-state';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { TimerService } from '../../../production-workspace/timer-badge/timer.service';
import { PersistenceService } from '../../../shared/misc-services/persistence.service';
import { RootUndoService } from '../../../shared/misc-services/root-undo.service';
import {
  AnnotationOperation,
  annotationQueueName,
  AnnotationResource,
} from '../../../shared/resources/annotation-resource.service';
import { ObjectChangeOperationService } from './object-change-operation.service';
import { ShapeChangeOperationService } from './shape-change-operation.service';
import { TaskChangeOperationService } from './task-change-operation.service';

@Injectable()
export class AnnotationChangeListenerService extends InjectableSuperclass {
  private undoService = inject(RootUndoService);
  private store = inject(TaskWorkspaceStore);
  private persistenceService = inject(PersistenceService);
  private annotationResource = inject(AnnotationResource);
  private shapeChangeOperationService = inject(ShapeChangeOperationService);
  private objectChangeOperationService = inject(ObjectChangeOperationService);
  private taskChangeOperationService = inject(TaskChangeOperationService);
  private timerService = inject(TimerService);

  private pendingOperations: AnnotationOperation[] = [];

  startListening() {
    // consider current annotations as the initial state
    this.undoService.reset();

    this.subscribeTo(
      this.undoService.state$.pipe(
        map(() => this.store.state()),
        pairwise(),
      ),
      ([previous, current]) => {
        this.sendChangeOperations(previous, current);
      },
    );
  }

  private sendChangeOperations(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    const batch = this.getAnnotationOperations(previous, current);
    if (isEmpty(batch)) {
      return;
    }

    this.pendingOperations.push(...batch);

    this.persistenceService.enqueue$(
      defer(() => {
        if (isEmpty(this.pendingOperations)) {
          return of(null);
        }

        const operationsToUpload = this.pendingOperations;
        this.pendingOperations = [];

        return this.annotationResource
          .uploadAnnotations$(operationsToUpload)
          .pipe(
            tap({
              error: () => {
                // on error, we re-add the uploaded operations to the pending
                // list so they can be picked up on retry
                this.pendingOperations.unshift(...operationsToUpload);
              },
            }),
          );
      }),
      annotationQueueName,
    );
  }

  private getAnnotationOperations(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    const batch = [
      ...this.shapeChangeOperationService.getOperations(previous, current),
      ...this.objectChangeOperationService.getOperations(previous, current),
      ...this.taskChangeOperationService.getOperations(previous, current),
    ];

    if (!isEmpty(batch)) {
      batch.push(this.getUpdateMetadataOperation());
    }

    return batch;
  }

  private getUpdateMetadataOperation(): UpdateMetadataOperation {
    return {
      operation: 'UPDATE_METADATA',
      attributes: {
        duration: this.timerService.elapsed(),
      },
    };
  }
}
